import Section from './_Section'
import Picture from '../helpers/Picture'
import TitleBlock from '../helpers/TitleBlock'
import createGradientElement from '../../helpers/markup'
import { useState, useEffect } from 'react'
import { transformPaddingToTailwind } from '../../helpers/style'
import ButtonEl from '../helpers/ctas/Button'

function FullBanner(props) {
  const alignment = props.contentAlignment?.toLowerCase() || 'left'
  const textAlignClass = alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-right' : 'text-left'

  // Prioritize Component Configuration images over Content images
  const mobileBgImage = props.mobileBackgroundImage || props.mobileImage
  const desktopBgImage = props.desktopBackgroundImage || props.desktopImage

  // Destructure to exclude background images from spread to ensure Component Configuration takes priority
  const { mobileBackgroundImage, desktopBackgroundImage, mobileImage, desktopImage, ...restProps } = props

  const titleBlockDescClass = '!mt-4' // pass to the description class in the TitleBlock inside of the Section component.

  return (
    <Section
      type="subPageBanner"
      outerClass={props.componentPadding}
      innerClass={`px-4 py-12 md:px-36 md:py-56 ${textAlignClass}`}
      {...restProps}
      mobileBackgroundImage={mobileBgImage}
      desktopBackgroundImage={desktopBgImage}
      titleBlockDescClass={titleBlockDescClass}
    />
  )
}

function SplitBannerContained(props) {
  const sectionGradient = ![null, 'None', 'Text area'].includes(props.gradientType)
  const contentGradient = ['Text area'].includes(props.gradientType)
  const [gradientColor, setGradientColor] = useState('#fff')
  const alignment = props.contentAlignment?.toLowerCase() || 'left'
  const textAlignClass = alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-right' : 'text-left'

  useEffect(() => {
    const bgColor = props.backgroundColor?.replace('#', '') || ''
    const fgColor = props.foregroundColor?.replace('#', '') || ''
    setGradientColor((prevState) => (fgColor ? `#${fgColor}` : prevState))
  }, [])

  // For split banner, don't pass images to Section (they should only appear in Picture component)
  const { mobileBackgroundImage, desktopBackgroundImage, mobileImage, desktopImage, ...restPropsWithoutImages } = props

  return (
    <Section
      type="subPageBanner"
      innerClass="flex flex-col-reverse lg:flex-row items-stretch p-0! max-w-none!"
      {...restPropsWithoutImages}
      sectionTitle={false}
      mobileBackgroundImage={null}
      desktopBackgroundImage={null}
    >
      {/* Mobile: Image top, Content bottom via flex-col-reverse */}
      {/* Desktop: Content left, Image right via flex-row */}

      {/* Content Section - Left 50% on Desktop, Bottom on Mobile */}
      <div
        className={`z-10 relative px-4 py-12 w-full lg:basis-1/2 lg:flex lg:min-h-[var(--banner-min-height,620px)] lg:p-0 lg:justify-center lg:items-center lg:self-stretch ${textAlignClass}`}
      >
        {sectionGradient && (
          <div className="block lg:hidden z-[-1] -ml-4 h-full absolute right-0 top-0 left-0">
            {createGradientElement({
              type: props.gradientType,
              fromColor: gradientColor,
              fullMobile: true,
            })}
          </div>
        )}

        <div className="w-full lg:px-24 lg:max-w-xl">
          {(props.title || props.description) && <TitleBlock {...props} descriptionClass={'!mt-4'} />}

          {props.ctaLink && props.ctaLink.length != 0 && (
            <div className={`flex flex-col md:flex-row justify-${textAlignment.flex} gap-4 md:gap-8 space-x-6 mt-10`}>
              {props.ctaLink.map((ctaLinkItem, i) => (
                <ButtonEl
                  key={i}
                  link={ctaLinkItem.link}
                  icon={ctaLinkItem.linkIconClass}
                  position={ctaLinkItem.linkPosition}
                  theme={ctaLinkItem.linkType.toLowerCase()}
                  className={`${!props.backgroundColor && ctaLinkItem.linkType.toLowerCase() === 'text' ? 'text-(--color-secondary)' : ''}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Image Section - Right 50% on Desktop, Top on Mobile */}
      <div className="lg:z-0 lg:basis-1/2 lg:relative lg:min-h-[var(--banner-min-height,620px)]">
        <Picture
          desktopImage={props.desktopImage || props.desktopBackgroundImage}
          mobileImage={props.mobileImage || props.mobileBackgroundImage}
          className={`w-full h-full object-cover rounded-none`}
        />
      </div>
    </Section>
  )
}

function SplitBannerFull(props) {
  const splitImage = props.variant === 'Split content and image'
  const contained = props.backgroundConfiguration === 'Contained'

  const moveImageClasses = splitImage && !contained ? 'bg-subBanner bg-no-repeat ' : ''
  const sectionGradient = ![null, 'None', 'Text area'].includes(props.gradientType)
  const contentGradient = ['Text area'].includes(props.gradientType)
  const [gradientColor, setGradientColor] = useState('#fff')
  const alignment = props.contentAlignment?.toLowerCase() || 'left'
  const textAlignClass = alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-right' : 'text-left'

  useEffect(() => {
    const bgColor = props.backgroundColor?.replace('#', '') || ''
    const fgColor = props.foregroundColor?.replace('#', '') || ''
    setGradientColor((prevState) => (fgColor ? `#${fgColor}` : prevState))
  }, [])

  // Prioritize Component Configuration images over Content images
  const mobileBgImage = props.mobileBackgroundImage || props.mobileImage
  const desktopBgImage = props.desktopBackgroundImage || props.desktopImage

  // Destructure to exclude background images from spread to ensure Component Configuration takes priority
  // Keep backgroundColor explicitly to ensure it's passed to Section component
  const { mobileBackgroundImage, desktopBackgroundImage, mobileImage, desktopImage, ...restProps } = props

  return (
    <Section
      type="subPageBanner"
      outerClass={`${moveImageClasses} py-0! split-full`}
      innerClass="p-0! max-w-none! mx-0!"
      {...restProps}
      backgroundColor={props.backgroundColor}
      sectionTitle={false}
      gradientType={null}
      mobileBackgroundImage={null}
      desktopBackgroundImage={null}
    >
      {/* Mobile: Image top, Content bottom via flex-col-reverse */}
      {/* Desktop: Content left, Image right via flex-row */}
      <div className="flex flex-col-reverse lg:flex-row w-full">
        {sectionGradient && (
          <div className="block lg:hidden z-[-1] h-full absolute right-0 top-0 left-0 ">
            {createGradientElement({
              type: props.gradientType,
              fromColor: gradientColor,
              fullMobile: true,
            })}{' '}
          </div>
        )}

        {/* Content Section - Left 50% on Desktop, Bottom on Mobile */}
        <div
          className={`px-4 py-12 lg:basis-1/2 lg:flex lg:min-h-[var(--banner-min-height,620px)] lg:p-0 lg:justify-center lg:items-center lg:self-stretch ${textAlignClass}`}
        >
          <div className="w-full lg:px-12 lg:max-w-xl">
            {(props.title || props.description) && <TitleBlock {...props} descriptionClass={'!mt-4'} />}

            {props.ctaLink && props.ctaLink.length != 0 && (
              <div className={`flex flex-col md:flex-row justify-left gap-4 md:gap-8 space-x-6 mt-10`}>
                {props.ctaLink.map((ctaLinkItem, i) => (
                  <ButtonEl
                    key={i}
                    link={ctaLinkItem.link}
                    icon={ctaLinkItem.linkIconClass}
                    position={ctaLinkItem.linkPosition}
                    theme={ctaLinkItem.linkType.toLowerCase()}
                    className={`${!props.backgroundColor && ctaLinkItem.linkType.toLowerCase() === 'text' ? 'text-(--color-secondary)' : ''}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Image Section - Right 50% on Desktop, Top on Mobile */}
        <div className="lg:basis-1/2 lg:relative lg:min-h-[var(--banner-min-height,620px)]">
          <Picture
            desktopImage={desktopImage || desktopBgImage}
            mobileImage={mobileImage || mobileBgImage}
            className={`w-full h-full object-cover`}
          />
        </div>
      </div>
    </Section>
  )
}

function SplitBanner(props) {
  const Comp = props.backgroundConfiguration === 'Contained' ? SplitBannerContained : SplitBannerFull

  return <Comp {...props} />
}

function SubPageBanner(props) {
  const componentPadding = transformPaddingToTailwind(props.componentPadding)
  const Comp = props.variant === 'Split content and image' ? SplitBanner : FullBanner

  return <Comp {...props} componentPadding={componentPadding} />
}

export default SubPageBanner
